// OS GRÁFICOS DA NYTA (24/09/2026).
//
// O servidor calcula o gráfico a partir do banco e o grava na mensagem (`anexos`); a fala só o
// cita com `[[grafico:<id>]]`. Número nenhum sai do modelo. Aqui mora o formato e a GEOMETRIA
// (escala, pontos, barras): a web desenha em <svg> e o app em react-native-svg, com a mesma conta.
//
// Dois formatos cobrem o que a Nyta mostra:
//   · `linha`: séries no tempo (cidades, ouvintes), com marcos (lançamento, show, ação concluída);
//   · `barras`: categorias com uma ou mais séries (parecidos, R·E·A·L antes e depois, cidades hoje).

export interface PontoDaSerie { x: string; y: number }
export interface SerieDaLinha { nome: string; pontos: PontoDaSerie[] }
export interface MarcoDaLinha { x: string; rotulo: string; tipo: 'lancamento' | 'show' | 'acao' }

export type GraficoDaConversa =
  | { tipo: 'linha'; titulo: string; unidade?: string; fonte: string; series: SerieDaLinha[]; marcos?: MarcoDaLinha[] }
  | { tipo: 'barras'; titulo: string; unidade?: string; fonte: string; categorias: string[]; series: Array<{ nome: string; valores: number[] }>; destaque?: number; maximo?: number };

export interface AnexoDaNyta { tipo: 'grafico'; grafico: GraficoDaConversa }

/** Quantos pontos uma linha precisa para ser uma linha: com um só, é um número, e não se desenha. */
export const PONTOS_MINIMOS = 2;

export const graficoTemDados = (g: GraficoDaConversa): boolean => (g.tipo === 'linha'
  ? g.series.some((s) => s.pontos.length >= PONTOS_MINIMOS)
  : g.categorias.length > 0 && g.series.some((s) => s.valores.some((v) => Number.isFinite(v))));

/** Um teto "redondo" para o eixo: 1, 2 ou 5 vezes uma potência de dez. */
export function tetoRedondo(maximo: number): number {
  if (!(maximo > 0)) return 1;
  const base = 10 ** Math.floor(Math.log10(maximo));
  const passo = [1, 2, 5, 10].find((m) => m * base >= maximo) ?? 10;
  return passo * base;
}

const dia = (x: string) => Date.parse(`${x.slice(0, 10)}T12:00:00Z`);

export interface GeometriaDaLinha {
  largura: number; altura: number;
  area: { x: number; y: number; largura: number; altura: number };
  teto: number;
  linhas: Array<{ nome: string; d: string; pontos: Array<{ x: number; y: number; valor: number; data: string }> }>;
  marcos: Array<{ x: number; rotulo: string; tipo: MarcoDaLinha['tipo'] }>;
  eixoY: Array<{ y: number; valor: number }>;
  eixoX: Array<{ x: number; data: string }>;
}

/** A linha desenhada numa caixa de `largura` × `altura`, com margem para os rótulos. */
export function geometriaDaLinha(g: Extract<GraficoDaConversa, { tipo: 'linha' }>, largura: number, altura: number): GeometriaDaLinha {
  const area = { x: 36, y: 8, largura: Math.max(1, largura - 44), altura: Math.max(1, altura - 30) };
  const todas = g.series.flatMap((s) => s.pontos);
  const datas = [...todas.map((p) => dia(p.x)), ...(g.marcos || []).map((m) => dia(m.x))].filter(Number.isFinite);
  const de = datas.length ? Math.min(...datas) : 0;
  const ate = datas.length ? Math.max(...datas) : 1;
  const span = ate - de || 1;
  const teto = tetoRedondo(Math.max(0, ...todas.map((p) => p.y)));
  const px = (x: string) => area.x + ((dia(x) - de) / span) * area.largura;
  const py = (y: number) => area.y + area.altura - (Math.max(0, y) / teto) * area.altura;

  const linhas = g.series.map((s) => {
    const pontos = [...s.pontos].sort((a, b) => dia(a.x) - dia(b.x))
      .map((p) => ({ x: px(p.x), y: py(p.y), valor: p.y, data: p.x.slice(0, 10) }));
    const d = pontos.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    return { nome: s.nome, d, pontos };
  });
  const eixoY = [0, 0.5, 1].map((f) => ({ y: py(teto * f), valor: teto * f }));
  const eixoX = datas.length ? [de, de + span / 2, ate].map((t) => {
    const data = new Date(t).toISOString().slice(0, 10);
    return { x: px(data), data };
  }) : [];
  const marcos = (g.marcos || []).filter((m) => Number.isFinite(dia(m.x))).map((m) => ({ x: px(m.x), rotulo: m.rotulo, tipo: m.tipo }));
  return { largura, altura, area, teto, linhas, marcos, eixoY, eixoX };
}

export interface GeometriaDasBarras {
  largura: number; altura: number; teto: number;
  linhas: Array<{ rotulo: string; y: number; barras: Array<{ x: number; y: number; largura: number; altura: number; valor: number; serie: number }>; destaque: boolean }>;
}

/**
 * Barras HORIZONTAIS: numa tela de celular, o rótulo da categoria (nome de cidade, de artista) cabe
 * na linha, e não precisa virar de lado. Cada categoria é uma faixa; cada série, uma barra nela.
 */
export function geometriaDasBarras(g: Extract<GraficoDaConversa, { tipo: 'barras' }>, largura: number): GeometriaDasBarras {
  const rotuloLargura = Math.min(140, Math.round(largura * 0.36));
  const valorLargura = 56;
  const util = Math.max(1, largura - rotuloLargura - valorLargura);
  const barra = 10;
  const entre = 4;
  const faixa = g.series.length * barra + (g.series.length - 1) * entre + 14;
  const teto = g.maximo ?? tetoRedondo(Math.max(0, ...g.series.flatMap((s) => s.valores.filter(Number.isFinite))));
  const linhas = g.categorias.map((rotulo, i) => ({
    rotulo,
    y: i * faixa,
    destaque: g.destaque === i,
    barras: g.series.map((s, j) => {
      const valor = Number.isFinite(s.valores[i]) ? s.valores[i] : 0;
      return { x: rotuloLargura, y: i * faixa + 7 + j * (barra + entre), largura: Math.max(0, (Math.max(0, valor) / teto) * util), altura: barra, valor, serie: j };
    }),
  }));
  return { largura, altura: g.categorias.length * faixa, teto, linhas };
}

/** O número como a tela mostra: 1,2 mil, 3,4 mi. */
export function numeroCurto(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const v = Math.abs(n);
  const sinal = n < 0 ? '-' : '';
  if (v >= 1e6) return `${sinal}${(v / 1e6).toFixed(1).replace(/\.0$/, '').replace('.', ',')} mi`;
  if (v >= 1e4) return `${sinal}${Math.round(v / 1e3)} mil`;
  if (v >= 1e3) return `${sinal}${(v / 1e3).toFixed(1).replace(/\.0$/, '').replace('.', ',')} mil`;
  return `${sinal}${Math.round(v * 10) / 10}`.replace('.', ',');
}
